import { Alert, Box, Button, Portal, Snackbar, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import AddSeasonDialog from "./components/AddSeasonDialog";
import CancelMatchesDialog from "./components/CancelMatchesDialog";
import FinishSeasonDialog from "./components/FinishSeasonDialog";
import ManageRankDialog from "./components/ManageRankDialog";
import NewRoundDialog from "./components/NewRoundDialog";
import RemoveRankPlayerDialog from "./components/RemoveRankPlayerDialog";
import SeasonAccordion from "./components/SeasonAccordion";
import SubstituteCoringaDialog from "./components/SubstituteCoringaDialog";
import useAdminMatches from "./hooks/useAdminMatches";

const Matches = () => {
  const {
    seasons,
    categories,
    lineDrafts,
    sentLines,
    cancelMatchesDialog,
    substituteCoringaDialog,
    removeRankPlayerDialog,
    addSeasonOpen,
    newSeasonName,
    finishModal,
    newRoundModal,
    manageRankModal,
    manageRankSearch,
    selectedSeasonForFinish,
    selectedSeasonForNewRound,
    selectedSeasonForManageRank,
    setAddSeasonOpen,
    setNewSeasonName,
    setFinishModal,
    setNewRoundModal,
    setManageRankSearch,
    canCancelGroup,
    openCancelMatchesDialog,
    closeCancelMatchesDialog,
    confirmCancelMatches,
    openSubstituteCoringaDialog,
    closeSubstituteCoringaDialog,
    toggleSubstitutePlayer,
    confirmSubstituteWithCoringa,
    closeRemoveRankPlayerDialog,
    confirmRemoveUserFromRank,
    getSubstituteCoringaView,
    getSeasonView,
    updateSeasonUi,
    handleToggleSeason,
    handleScoreChange,
    lineHasChanges,
    canSendLine,
    handleSendLine,
    handleAddSeason,
    handleFinishSeason,
    handleCreateNewRound,
    handleOpenManageRank,
    handleCloseManageRank,
    handleManageRankUserCategoryChange,
    manageRankPlayerCategory,
    handleAddUserToRank,
    handleRemoveUserFromRank,
    getManageRankView,
  } = useAdminMatches();

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSendLineWithFeedback = async (lineId, line) => {
    console.log("Sending line", lineId, line);
    try {
      const result = await handleSendLine(lineId, line);
      console.log("result", result);
      if (result?.error) {
        setSnackbar({ open: true, message: result.error, severity: "error" });
      }
    } catch (error) {
      console.log("result error", error);
      setSnackbar({
        open: true,
        message: error?.message || "Erro ao enviar resultados.",
        severity: "error",
      });
    }
  };

  const handleConfirmCancelMatches = async () => {
    try {
      const result = await confirmCancelMatches();
      if (result?.message) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Erro ao cancelar partidas.",
        severity: "error",
      });
    }
  };

  const handleConfirmSubstituteCoringa = async () => {
    try {
      const result = await confirmSubstituteWithCoringa();
      if (result?.message) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: result.severity || "success",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Erro ao substituir jogadores.",
        severity: "error",
      });
    }
  };

  const manageRankView = getManageRankView();
  const manageRankSeasonId = manageRankView.seasonId;
  const substituteCoringaView = getSubstituteCoringaView();

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
            Rodadas
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Gerencie rodadas, resultados e rankings da competição.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddSeasonOpen(true)}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Criar Ranking
        </Button>
      </Stack>

      <Stack spacing={1.5}>
        {seasons.map((season) => {
          const { ui, seasonResult, lines, membersCount, rounds } =
            getSeasonView(season);

          return (
            <SeasonAccordion
              key={season.id}
              season={season}
              ui={ui}
              seasonResult={seasonResult}
              lines={lines}
              membersCount={membersCount}
              sentLines={sentLines}
              lineDrafts={lineDrafts}
              categories={categories}
              rounds={rounds}
              onToggleSeason={handleToggleSeason}
              onUpdateSeasonUi={updateSeasonUi}
              onOpenFinishSeason={(seasonId) =>
                setFinishModal({ open: true, seasonId })
              }
              onOpenNewRound={(seasonId) =>
                setNewRoundModal({ open: true, seasonId, status: null })
              }
              onOpenManageRank={handleOpenManageRank}
              onOpenCancelMatches={openCancelMatchesDialog}
              onOpenSubstituteCoringa={openSubstituteCoringaDialog}
              onScoreChange={handleScoreChange}
              lineHasChanges={lineHasChanges}
              canSendLine={canSendLine}
              canCancelGroup={canCancelGroup}
              isCancellingMatches={cancelMatchesDialog.loading}
              isSubstitutingCoringa={substituteCoringaDialog.loading}
              onSendLine={handleSendLineWithFeedback}
            />
          );
        })}
      </Stack>

      <ManageRankDialog
        open={manageRankModal.open}
        seasonName={selectedSeasonForManageRank?.name}
        categories={categories}
        playerCategoryById={manageRankPlayerCategory}
        onChangeUserCategory={handleManageRankUserCategoryChange}
        search={manageRankSearch}
        onChangeSearch={setManageRankSearch}
        usersInRank={manageRankView.usersInRank}
        usersOutRank={manageRankView.usersOutRank}
        onRemove={(user) =>
          manageRankSeasonId &&
          handleRemoveUserFromRank(manageRankSeasonId, user)
        }
        onAdd={(userId, categoryId) =>
          manageRankSeasonId &&
          handleAddUserToRank(manageRankSeasonId, userId, categoryId)
        }
        onClose={handleCloseManageRank}
      />

      <AddSeasonDialog
        open={addSeasonOpen}
        seasonName={newSeasonName}
        onChangeSeasonName={setNewSeasonName}
        onClose={() => setAddSeasonOpen(false)}
        onConfirm={handleAddSeason}
      />

      <FinishSeasonDialog
        open={finishModal.open}
        seasonName={selectedSeasonForFinish?.name}
        onClose={() => setFinishModal({ open: false, seasonId: null })}
        onConfirm={handleFinishSeason}
      />

      <NewRoundDialog
        open={newRoundModal.open}
        seasonName={selectedSeasonForNewRound?.name}
        status={newRoundModal.status}
        errorMessage={newRoundModal.errorMessage}
        endsAt={newRoundModal.endsAt}
        onEndsAtChange={(value) =>
          setNewRoundModal((prev) => ({ ...prev, endsAt: value }))
        }
        onClose={() =>
          setNewRoundModal({
            open: false,
            seasonId: null,
            status: null,
            errorMessage: null,
            endsAt: null,
          })
        }
        onConfirm={handleCreateNewRound}
      />

      <CancelMatchesDialog
        open={cancelMatchesDialog.open}
        matchesCount={cancelMatchesDialog.line?.games?.length || 0}
        loading={cancelMatchesDialog.loading}
        errorMessage={cancelMatchesDialog.errorMessage}
        onClose={closeCancelMatchesDialog}
        onConfirm={handleConfirmCancelMatches}
      />

      <RemoveRankPlayerDialog
        open={removeRankPlayerDialog.open}
        playerName={removeRankPlayerDialog.player?.name}
        loading={removeRankPlayerDialog.loading}
        errorMessage={removeRankPlayerDialog.errorMessage}
        onClose={closeRemoveRankPlayerDialog}
        onConfirm={confirmRemoveUserFromRank}
      />

      <SubstituteCoringaDialog
        open={substituteCoringaDialog.open}
        players={substituteCoringaView.players}
        availableCoringas={substituteCoringaView.availableCoringas}
        selectedPlayerIds={substituteCoringaView.selectedPlayerIds}
        replacementPreview={substituteCoringaView.replacementPreview}
        loading={substituteCoringaDialog.loading}
        errorMessage={substituteCoringaDialog.errorMessage}
        onTogglePlayer={toggleSubstitutePlayer}
        onClose={closeSubstituteCoringaDialog}
        onConfirm={handleConfirmSubstituteCoringa}
      />

      <Portal>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={(_, reason) => {
            if (reason === "clickaway") return;
            setSnackbar((prev) => ({ ...prev, open: false }));
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ maxWidth: 520 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Stack>
  );
};

export default Matches;
